/* eslint-disable react/require-default-props */
/* eslint-disable react/jsx-curly-newline */
import React, { useRef, useState } from 'react';
import { useDetectScroll, useResponsiveValueState } from '@saviynt/common';
import {
  Box,
  Button,
  FooterActionBar,
  Icon,
  InputField,
  ModalIsland,
  Switch,
  Tooltip,
  Typography,
} from '@saviynt/design-system';

import type { RuleAction } from '../../../services';
import type { InvertedLocalize } from '../../../utils/localization';

import { useUserDisplayName } from '../../../shared/hooks';
import { useInvertedLocalize } from '../../../utils/localization';

import invertedMessages, { type ActivateRuleModalMessage } from './messages';

import './ActivateRuleModal.css';

interface ActivateRuleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (formData: ActivateRuleFormData) => void;
  /** Callback when Save & Activate button is clicked */
  onSaveAndActivate: (
    formData: ActivateRuleFormData,
    actions: RuleAction[]
  ) => void;
  isSaving: boolean;
  isActivating: boolean;
  actions?: RuleAction[]; // Rule actions/entitlements from the rule data
  errorMessage?: string; // Error message to display under rule name input
  onClearError?: () => void;
}

export interface ActivateRuleFormData {
  ruleName: string;
  description: string;
  isBirthrightAccess: boolean;
  isRemoveAccessWhenNotSatisfied: boolean;
  // isCreateMatchingRole: boolean; // mvp2+
}

export type LocalizeFn = InvertedLocalize<ActivateRuleModalMessage>;

function ActivateRuleModal({
  isOpen,
  onClose,
  onSave,
  onSaveAndActivate,
  isSaving = false,
  isActivating = false,
  actions = [],
  errorMessage = '',
  onClearError,
}: ActivateRuleModalProps): JSX.Element {
  const [ruleName, setRuleName] = useState('');
  const [description, setDescription] = useState('');
  const [isAdvancedOptionsExpanded, setIsAdvancedOptionsExpanded] =
    useState(false);

  // Advanced options state
  const [isBirthrightAccess, setIsBirthrightAccess] = useState(true);
  const [isRemoveAccessWhenNotSatisfied, setIsRemoveAccessWhenNotSatisfied] =
    useState(false);
  // const [isCreateMatchingRole, setIsCreateMatchingRole] = useState(false); // mvp2+

  const l = useInvertedLocalize(invertedMessages);

  const userDisplayName = useUserDisplayName();

  // Responsive and scroll detection setup
  const [mediaQueryValue, setMediaQueryValue] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);

  const SCREEN_WIDTH_QUERIES = [
    { query: '(max-width: 599px)', value: 110, label: 'MobileXLarge' },
  ];

  useResponsiveValueState(setMediaQueryValue, SCREEN_WIDTH_QUERIES);
  const mobileXlargeQuery = SCREEN_WIDTH_QUERIES.find(
    (item) => item.label === 'MobileXLarge'
  );
  const isMobileXlargeWidth =
    (mobileXlargeQuery?.value ?? 0) <= mediaQueryValue;

  const { isScrolled: sectionRefIsScrolled } = useDetectScroll(
    null,
    sectionRef
  );

  // Form validation - buttons enabled only when both name and description are filled
  const isFormValid = ruleName.trim() !== '' && description.trim() !== '';

  // Reset form when modal closes
  const handleClose = () => {
    setRuleName('');
    setDescription('');
    setIsAdvancedOptionsExpanded(false);
    setIsBirthrightAccess(true);
    setIsRemoveAccessWhenNotSatisfied(false);
    // setIsCreateMatchingRole(false);
    onClose();
  };

  // Handle save actions
  const handleSave = () => {
    if (!isFormValid) {
      return;
    }

    const formData: ActivateRuleFormData = {
      ruleName: ruleName.trim(),
      description: description.trim(),
      isBirthrightAccess,
      isRemoveAccessWhenNotSatisfied,
      // isCreateMatchingRole, // mvp2+
    };

    onSave(formData);
  };

  const handleSaveAndActivate = () => {
    if (!isFormValid) {
      return;
    }

    const formData: ActivateRuleFormData = {
      ruleName: ruleName.trim(),
      description: description.trim(),
      isBirthrightAccess,
      isRemoveAccessWhenNotSatisfied,
      // isCreateMatchingRole, // mvp2+
    };

    onSaveAndActivate(formData, actions);
  };

  // Toggle advanced options
  const toggleAdvancedOptions = () => {
    setIsAdvancedOptionsExpanded(!isAdvancedOptionsExpanded);
  };

  // Handle keyboard events for advanced options toggle
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleAdvancedOptions();
    }
  };

  // Modal content body
  const contentBody = (
    <div className='Rules-ActivateRuleModal-content' ref={sectionRef}>
      <Typography
        kind='body2'
        color='--color-foreground-neutral-medium'
        className='Rules-ActivateRuleModal-description'>
        {l('Please provide the details below to save and activate the rule')}
      </Typography>

      {/* Form fields */}
      <Box className='Rules-ActivateRuleModal-form'>
        <Box className='Rules-ActivateRuleModal-field'>
          <Typography kind='label' htmlFor='ruleName'>
            {l('Rule Name')}
          </Typography>
          <InputField
            isRequired
            maxLength={250}
            name='ruleName'
            placeholder={l('Give this rule a unique name')}
            value={ruleName}
            setValue={(value: string) => {
              setRuleName(value);

              if (errorMessage && onClearError) {
                onClearError();
              }
            }}
            isCritical={!!errorMessage}
            CriticalHelperText={errorMessage}
            className='Rules-ActivateRuleModal-input'
          />
        </Box>

        <Box className='Rules-ActivateRuleModal-field'>
          <Typography kind='label' htmlFor='description'>
            {l('Description')}
          </Typography>
          <InputField
            isRequired
            maxLength={2000}
            name='description'
            placeholder={l('Provide a brief description for your rule')}
            value={description}
            setValue={setDescription}
            className='Rules-ActivateRuleModal-input'
          />
        </Box>

        <Box className='Rules-ActivateRuleModal-field'>
          <Typography kind='label' htmlFor='owner'>
            {l('Owner')}
          </Typography>
          <InputField
            name='owner'
            placeholder=''
            value={userDisplayName}
            isReadOnly
            isDisabled
            className='Rules-ActivateRuleModal-input'
          />
        </Box>
      </Box>

      {/* Advanced Options */}
      <Box className='Rules-ActivateRuleModal-advancedOptions'>
        <div
          className='Rules-ActivateRuleModal-advancedOptionsHeader'
          onClick={toggleAdvancedOptions}
          role='button'
          tabIndex={0}
          onKeyDown={handleKeyDown}
          aria-expanded={isAdvancedOptionsExpanded}>
          <Icon
            kind='ChevronDown'
            rotate={isAdvancedOptionsExpanded ? '180' : '0'}
            size='medium'
            className='Rules-ActivateRuleModal-chevron'
          />
          <Typography kind='h3'>{l('Advanced Options')}</Typography>
        </div>

        {/* Advanced Options Content */}
        <Box
          className={`Rules-ActivateRuleModal-advancedOptionsContent ${
            isAdvancedOptionsExpanded
              ? 'Rules-ActivateRuleModal-advancedOptionsContent--isVisible'
              : 'Rules-ActivateRuleModal-advancedOptionsContent--isHidden'
          }`}
          aria-hidden={!isAdvancedOptionsExpanded}>
          {/* Birthright Access Switch */}
          <Box className='Rules-ActivateRuleModal-switchRow'>
            <Switch
              isChecked={isBirthrightAccess}
              onChange={() => setIsBirthrightAccess(!isBirthrightAccess)}
              label={l('Birthright Access')}
              labelPosition='right'
              className='Rules-ActivateRuleModal-switch'
            />
            <Tooltip
              text={l('Mark this Technical Rule as a Birthright Rule')}
              placement='top'>
              <Icon
                kind='AlertInfoFilled'
                size='medium'
                color='--color-foreground-info-subtle'
                className='Rules-ActivateRuleModal-infoIcon'
              />
            </Tooltip>
          </Box>

          {/* Remove Access Switch */}
          <Box className='Rules-ActivateRuleModal-switchRow'>
            <Switch
              isDisabled={!isBirthrightAccess}
              isChecked={isRemoveAccessWhenNotSatisfied}
              onChange={() =>
                setIsRemoveAccessWhenNotSatisfied(
                  !isRemoveAccessWhenNotSatisfied
                )
              }
              label={l("Remove access when user doesn't satisfy this rule")}
              labelPosition='right'
              className='Rules-ActivateRuleModal-switch'
            />
            <Tooltip
              text={l(
                'Remove access assigned through the Birthright Rule if the rule conditions are no longer met'
              )}
              placement='top'>
              <Icon
                kind='AlertInfoFilled'
                size='medium'
                color='--color-foreground-info-subtle'
                className='Rules-ActivateRuleModal-infoIcon'
              />
            </Tooltip>
          </Box>

          {/* Create Matching Role Switch - mvp2+ */}
          {/* <Box className='Rules-ActivateRuleModal-switchRow'>
              <Switch
                isChecked={isCreateMatchingRole}
                onChange={() => setIsCreateMatchingRole(!isCreateMatchingRole)}
                label={l('Create matching Role')}
                labelPosition='right'
                className='Rules-ActivateRuleModal-switch'
              />
              <Icon
                kind='AlertInfoFilled'
                size='medium'
                color='--color-foreground-info-subtle'
                className='Rules-ActivateRuleModal-infoIcon'
              />
            </Box> */}
        </Box>
      </Box>
    </div>
  );

  // Footer with action buttons
  const footerActionBar = (
    <Box className='Rules-ActivateRuleModal-footer'>
      <Box className='Rules-ActivateRuleModal-footerButtons'>
        <Button
          kind='outlined'
          size='medium'
          onClick={handleSave}
          isDisabled={!isFormValid || isSaving || isActivating}
          isLoading={isSaving}
          className='Rules-ActivateRuleModal-saveButton'>
          {l('Save')}
        </Button>
        <Button
          kind='filled'
          leftIcon={<Icon kind='QuickAction' />}
          size='medium'
          onClick={handleSaveAndActivate}
          isDisabled={!isFormValid || isSaving || isActivating}
          isLoading={isActivating}
          className='Rules-ActivateRuleModal-activateButton'>
          {l('Save & Activate')}
        </Button>
      </Box>
    </Box>
  );

  return (
    <ModalIsland
      isOpen={isOpen}
      onClose={handleClose}
      isMobileWidth={isMobileXlargeWidth}
      title={l('Save and Activate Rule')}
      headerIcon={
        <Icon
          kind='QuickAction'
          color='--color-foreground-neutral-bold'
          size='medium'
        />
      }
      contentBody={contentBody}
      sectionRef={sectionRef}
      FooterActionBarComp={
        <FooterActionBar size='large' isScrolled={sectionRefIsScrolled}>
          {footerActionBar}
        </FooterActionBar>
      }
      className='Rules-ActivateRuleModal'
    />
  );
}

ActivateRuleModal.defaultProps = {
  actions: [],
};

export default ActivateRuleModal;
